/**
 * Dashboard Statistics Component
 *
 * Displays role-based dashboard statistics with responsive layout.
 * Fetches data through TanStack Query and displays:
 * - Super Admin: Organization and user counts
 * - Admin: User counts in their organization
 * - Mentor: Student count
 * - Student: Empty state
 */

import { useEffect } from 'react';
import { useScreenWithOrchestrator } from '@/hooks/useScreenWithOrchestrator';
import { ScreenContainer } from '@/components/core/ScreenContainer';
import { ScreenType } from '@/types/screen.types';
import {
  GetDashboardStatsOutput,
  SuperAdminStats,
  AdminStats,
  MentorStats,
} from '@/types/dashboard-stats.types';
import { UserRole } from '@/types/enums/UserRole';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { useDashboardStatsQuery } from '@/hooks/queries/useDashboardStatsQuery';
import { QueryError } from '@/lib/execute-query';
import { ResponseCode } from '@/business/types/response-codes.types';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  iconBgColor: string;
}

function StatCard({ icon, label, count, iconBgColor }: StatCardProps) {
  const colors = useThemeColors();

  return (
    <div
      className="rounded-xl shadow-soft p-6 hover:shadow-soft-md transition-all duration-300"
      style={{ backgroundColor: colors.offWhite }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p
            className="text-sm font-medium mb-2"
            style={{ color: colors.text.secondary }}
          >
            {label}
          </p>
          <p
            className="text-3xl font-semibold"
            style={{ color: colors.text.primary }}
          >
            {count}
          </p>
        </div>
        <div
          className="rounded-full p-3"
          style={{ backgroundColor: iconBgColor, color: colors.text.inverse }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function DashboardStats() {
  const colors = useThemeColors();
  const {
    data: statsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useDashboardStatsQuery();

  const screen = useScreenWithOrchestrator<GetDashboardStatsOutput>({
    metadata: {
      screenType: ScreenType.DASHBOARD,
      requiresAuth: true,
      showHeader: false,
      showSidebar: false,
    },
    onLoad: async (_strategy, screenState) => {
      screenState.setLoading();
    },
  });

  // Drive screen state from TQ
  useEffect(() => {
    if (isLoading) {
      screen.setLoading();
      return;
    }
    if (isError) {
      const code =
        error instanceof QueryError ? error.code : ResponseCode.INTERNAL_ERROR;
      screen.setError({
        code,
        message: 'Failed to load dashboard statistics',
        canRetry: true,
        retryAction: () => {
          refetch();
        },
      });
      return;
    }
    if (statsData) {
      screen.setSuccess();
    } else {
      screen.setEmpty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError, statsData]);

  const renderStats = (data: GetDashboardStatsOutput) => {
    // Check if admin with SuperAdminStats (has org_count field)
    if (
      data.role === UserRole.ADMIN &&
      'org_count' in (data.stats as SuperAdminStats)
    ) {
      const stats = data.stats as SuperAdminStats;
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <StatCard
            icon={<Icon name={IconName.BUILDING} size="lg" />}
            label="Total Organizations"
            count={stats.org_count}
            iconBgColor={colors.purple}
          />
          <StatCard
            icon={<Icon name={IconName.USERS} size="lg" />}
            label="Total Users"
            count={stats.total_users}
            iconBgColor={colors.blue}
          />
          <StatCard
            icon={<Icon name={IconName.USER_CHECK} size="lg" />}
            label="Total Admins"
            count={stats.total_admins}
            iconBgColor={colors.primary}
          />
          <StatCard
            icon={<Icon name={IconName.GRADUATION_CAP} size="lg" />}
            label="Total Mentors"
            count={stats.total_mentors}
            iconBgColor={colors.success}
          />
          <StatCard
            icon={<Icon name={IconName.BOOK_OPEN} size="lg" />}
            label="Total Students"
            count={stats.total_students}
            iconBgColor={colors.rust}
          />
        </div>
      );
    } else if (data.role === UserRole.ADMIN) {
      const stats = data.stats as AdminStats;
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            icon={<Icon name={IconName.USERS} size="lg" />}
            label="Total Users"
            count={stats.total_users}
            iconBgColor={colors.blue}
          />
          <StatCard
            icon={<Icon name={IconName.GRADUATION_CAP} size="lg" />}
            label="Mentors"
            count={stats.total_mentors}
            iconBgColor={colors.success}
          />
          <StatCard
            icon={<Icon name={IconName.BOOK_OPEN} size="lg" />}
            label="Students"
            count={stats.total_students}
            iconBgColor={colors.rust}
          />
        </div>
      );
    } else if (data.role === UserRole.MENTOR) {
      const stats = data.stats as MentorStats;
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            icon={<Icon name={IconName.BOOK_OPEN} size="lg" />}
            label="My Students"
            count={stats.total_students}
            iconBgColor={colors.rust}
          />
        </div>
      );
    } else {
      // Student role - empty state
      return (
        <div
          className="rounded-xl shadow-soft p-12 text-center"
          style={{ backgroundColor: colors.offWhite }}
        >
          <div className="mb-4 flex justify-center">
            <div
              className="rounded-full p-4"
              style={{ backgroundColor: colors.blue + '1A' }}
            >
              <Icon name={IconName.BOOK_OPEN} size="xl" color={colors.blue} />
            </div>
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text.primary }}
          >
            Welcome to Your Dashboard
          </h3>
          <p style={{ color: colors.text.secondary }}>
            Your learning journey starts here. Check back soon for updates!
          </p>
        </div>
      );
    }
  };

  return (
    <ScreenContainer
      state={screen.state}
      error={screen.error}
      onRetry={() => refetch()}
      emptyState={{
        title: 'No statistics',
        description: 'No statistics available',
      }}
    >
      <div className="p-8">
        {statsData &&
          renderStats(statsData as unknown as GetDashboardStatsOutput)}
      </div>
    </ScreenContainer>
  );
}
